<script setup>
import { computed, defineProps } from 'vue'

const props = defineProps({
  // single card from your ProductController stream
  card: {
    type: Object,
    default: () => ({}),
  },
  // optional override, if you ever want to pass settings explicitly
  settings: {
    type: Object,
    default: () => ({}),
  },
})

/**
 * Normalize settings:
 *  - explicit prop.settings
 *  - else card.settings
 *  - else {}
 */
const settingsNormalized = computed(() => {
  if (props.settings && Object.keys(props.settings).length) {
    return props.settings
  }
  if (props.card && props.card.settings && Object.keys(props.card.settings).length) {
    return props.card.settings
  }
  return {}
})

const rootClasses = computed(() => {
  const s = settingsNormalized.value
  return [
    'content-item',
    s.hideForABTest ? '-hide-ab-test' : '',
    s.hideMobile ? '-hide-mobile' : '',
    s.hideTablet ? '-hide-tablet' : '',
    s.hideDesktop ? '-hide-desktop' : '',
  ]
    .filter(Boolean)
    .join(' ')
})

const moduleClasses = computed(() => {
  const s = settingsNormalized.value
  return [
    'module-card',
    'module-card--default',
    s.centeredCards ? '-center-cards' : '',
  ]
    .filter(Boolean)
    .join(' ')
})

/**
 * Get image for a single card.
 * Uses flattened media[] first, falls back to card.image if available.
 */
const getImageForCard = (card) => {
  if (!card) return null

  const media = Array.isArray(card.media) ? card.media : []
  const fromMedia = media.find(
    (m) => (m.field || '').toLowerCase() === 'image',
  )
  if (fromMedia) {
    return {
      url: fromMedia.url,
      alt: fromMedia.alt || '',
      title: fromMedia.title || '',
    }
  }

  if (card.image?.url) {
    return {
      url: card.image.url,
      alt: card.image.alternative || '',
      title: card.image.title || '',
    }
  }

  return null
}

const hasCard = computed(() => props.card && Object.keys(props.card).length > 0)
</script>

<template>
  <div :class="rootClasses">
    <div
      :class="moduleClasses"
      :id="settingsNormalized.anchor || null"
    >
      <template v-if="hasCard">
        <component
          :is="props.card.link ? 'a' : 'div'"
          class="card-wrapper"
          :href="props.card.link || undefined"
        >
          <div class="card__content">
            <div class="content__image">
              <div
                v-if="getImageForCard(props.card)"
                class="image__image"
              >
                <img
                  :src="getImageForCard(props.card).url"
                  :alt="getImageForCard(props.card).alt || props.card.title || ''"
                  :title="getImageForCard(props.card).title || ''"
                  loading="lazy"
                />
              </div>
            </div>

            <div class="content__content">
              <div class="content__title">
                <div
                  class="title__title"
                  :class="settingsNormalized.alignmentTitle"
                >
                  <component
                    :is="settingsNormalized.headerType || 'h3'"
                    class="heading beta"
                    :class="settingsNormalized.colorTitle"
                  >
                    {{ props.card.title }}
                  </component>
                </div>

                <div
                  v-if="props.card.subtitle"
                  class="title__subtitle"
                  :class="settingsNormalized.alignmentSubtitle"
                >
                  <component
                    :is="settingsNormalized.subHeaderType || 'h4'"
                    class="heading gamma"
                    :class="settingsNormalized.colorSubtitle"
                  >
                    {{ props.card.subtitle }}
                  </component>
                </div>
              </div>

              <div
                class="content__text"
                v-html="props.card.bodytext"
              ></div>
            </div>
          </div>
        </component>
      </template>
    </div>
  </div>
</template>
